import { Group, Text, Badge, Flex, TableTd } from "@mantine/core";
import { IconGripVertical } from "@tabler/icons-react";
import {
  TableActions,
  EditAction,
  DeleteAction,
  actionsColumn,
} from "../TableActions";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { DataTableDraggableRow } from "mantine-datatable";
import { DataTable } from "../DataTable";

import { Ionicon } from "../Ionicon";
import { useDeletePage, useUpdatePagesOrder } from "../../api/pages";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";

function PagesTable({ data, pagination }) {
  const { mutate: deleteMutate, isPending } = useDeletePage();
  const confirmDelete = useDeleteConfirm("page", deleteMutate);
  const { mutate: updateOrder, isPending: isUpdating } = useUpdatePagesOrder();

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(data.data);
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    // Update the order of all affected items
    const updates = items.map((item, index) => ({
      id: item.id,
      order: index + 1,
    }));

    // Check if the order has changed by comparing with original data
    const hasOrderChanged = updates.some((update) => {
      const originalItem = data.data.find((item) => item.id === update.id);
      return originalItem.order !== update.order;
    });

    if (hasOrderChanged) {
      updateOrder({ updates });
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <DataTable
        records={data?.data}
        columns={[
          {
            accessor: "",
            width: 40,
            hiddenContent: true,
          },
          {
            accessor: "order",
            width: 75,
            textAlign: "center",
            render: (row) => <Text size="sm">{row.order}</Text>,
          },
          {
            accessor: "icon",
            width: 75,
            textAlign: "center",
            render: (row) => (
              <Flex align="center" justify="center">
                <Ionicon name={row.icon} styles={{ root: { width: 24 } }} />
              </Flex>
            ),
          },
          {
            accessor: "title",
            render: (row) => (
              <Group>
                <Text size="sm">{row.title}</Text>
                {row.isDraft && (
                  <Badge variant="dot" color="grey">
                    DRAFT
                  </Badge>
                )}
              </Group>
            ),
          },
          actionsColumn((row) => (
            <TableActions>
              <EditAction to={`${row.id}`} />
              <DeleteAction onDelete={() => confirmDelete({ id: row.id })} />
            </TableActions>
          )),
        ]}
        page={pagination.data.page}
        onPageChange={(page) =>
          pagination.setPagination({ ...pagination.data, page })
        }
        fetching={isUpdating || isPending}
        recordsPerPage={data?.pagination.perPage}
        totalRecords={data?.pagination.total}
        tableWrapper={({ children }) => (
          <Droppable droppableId="datatable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {children}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
        rowFactory={({ record, index, rowProps, children }) => (
          <Draggable
            key={record.id}
            draggableId={record.id.toString()}
            index={index}
          >
            {(provided, snapshot) => (
              <DataTableDraggableRow
                isDragging={snapshot.isDragging}
                {...rowProps}
                {...provided.draggableProps}
              >
                <TableTd {...provided.dragHandleProps} ref={provided.innerRef}>
                  <IconGripVertical size={16} />
                </TableTd>
                {children}
              </DataTableDraggableRow>
            )}
          </Draggable>
        )}
      />
    </DragDropContext>
  );
}

export default PagesTable;
